<script setup lang="ts">
import texts from './encrypted_texts.json'
import { ref, computed, onBeforeMount } from 'vue'
import AES from 'crypto-js/aes'
import Utf8 from 'crypto-js/enc-utf8'
import Passcode from '@/components/Passcode.vue'
import ImgCarousel from '@/components/ImgCarousel.vue'
import Timeline from '@/components/Timeline.vue'
import type { TTexts } from './types'

import pkg from '../package.json'

const secret = ref('')
const textContent = ref(texts)

const dt = computed<TTexts>(() => {
  const decryptedTextsObject: TTexts = {}
  const decryptText = (key: string) => {
    const encryptedText = texts[key as keyof typeof texts]
    const decryptedText = AES.decrypt(encryptedText, secret.value).toString(Utf8)
    decryptedTextsObject[key as keyof typeof texts] = decryptedText
  }

  const t = import.meta.env.DEV ? textContent.value : textContent.value
  if (import.meta.env.DEV) {
    return t
  } else {
    try {
      Object.keys(t).forEach(decryptText)
    } catch (error) {
      console.error('Decryption failed:', error)
      return decryptedTextsObject // Return an empty object if decryption fails
    }
  }

  return decryptedTextsObject
})

onBeforeMount(async () => {
  if (import.meta.env.DEV) {
    import('./texts.json').then((module) => {
      textContent.value = module.default
    })
  }
})
</script>

<template>
  <Passcode v-model="secret" />
  <div id="app-container1">
    <ImgCarousel :secret="secret" :texts="dt"></ImgCarousel>
    <div id="texts-container">
      <div id="main-top-text-container" class="text text-container bg-primary">
        <h1>{{ dt.greeting }}</h1>
        <h4>{{ dt.subgreeting }}</h4>
      </div>

      <div class="bg-tertiary centered" style="margin-top: 80px; margin-bottom: 50px;">
        <h2 style="margin-bottom: 0;">{{ dt.tlHeading }}</h2>
        <Timeline :texts="dt" class="text-container"></Timeline>
      </div>

      <div class="text text-container bg-tertiary">
        <h2>{{ dt.parking }}</h2>
        <p>{{ dt.parkingHint }}</p>
        <div>
          <ul>
            <li>
              <a :href="dt.parkingLink1" target="_blank">
                {{ dt.parkingLinkHint1 }}
                <i class="pi pi-external-link" style="font-size: 0.8rem"></i>
              </a>
            </li>
            <li>
              <a :href="dt.parkingLink2" target="_blank">
                {{ dt.parkingLinkHint2 }}
                <i class="pi pi-external-link" style="font-size: 0.8rem"></i>
              </a>
            </li>
            <li>
              <a :href="dt.parkingLink3" target="_blank">
                {{ dt.parkingLinkHint3 }}
                <i class="pi pi-external-link" style="font-size: 0.8rem"></i>
              </a>
            </li>
          </ul>
        </div>
        <div>
          <a :href="dt.routeToLocationLink" target="_blank">
            {{ dt.routeToLocationHint }}
            <i class="pi pi-external-link" style="font-size: 0.8rem"></i>
          </a>
        </div>
      </div>
      <div class="text text-container bg-tertiary">
        <h2>{{ dt.cake }}</h2>
        <p>{{ dt.cakeHint }}</p>
        <div>
          <ul>
            <li>
              <a :href="`tel:${dt.cakeContactTel}`">{{ dt.cakeContact }}: {{ dt.cakeContactTel }}</a>
            </li>
          </ul>
        </div>
      </div>

      <div class="text text-container bg-tertiary">
        <h2>{{ dt.contributions }}</h2>
        <p>{{ dt.presents }}</p>
        <p>{{ dt.contributionsHint }}</p>
        <div>
          <ul>
            <li>
              <a :href="`tel:${dt.contact1Tel}`">{{ dt.contact1 }}: {{ dt.contact1Tel }}</a>
            </li>
            <li>
              <a :href="`tel:${dt.contact2Tel}`">{{ dt.contact2 }}: {{ dt.contact2Tel }}</a>
            </li>
          </ul>
        </div>
        <div class="text text-container">
          <div>
            <h2>{{ dt.nightHeading }}</h2>
            <p>{{ dt.nightText }}</p>
            <ul>
              <li>
                <a :href="dt.night1link" target="_blank">
                  {{ dt.night1name }} - {{ dt.night1info }}
                  <i class="pi pi-external-link" style="font-size: 0.8rem"></i>
                </a>
              </li>
              <li>
                <a :href="dt.night2link" target="_blank">
                  {{ dt.night2name }} - {{ dt.night2info }}
                  <i class="pi pi-external-link" style="font-size: 0.8rem"></i>
                </a>
              </li>
            </ul>
            <p>{{ dt.nightText2 }}</p>
          </div>
        </div>
      </div>
    </div>

  </div>
  <div id="footer-made-by" :title="`Version: ${pkg.version}`">
    Made with 🤍 by Sara & Philipp
  </div>
</template>

<style scoped lang="scss">
#main-top-text-container {
  @media (min-width: $breakpoint-tablet) {
    border-radius: 0 0 24px 24px;
  }
}

#app-container1 {
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: calc(var(--default-space) * 2);
  align-items: center;
  justify-content: flex-start;
  min-width: 250px;
  min-height: 100vh;
  box-sizing: border-box;
  padding: 0;

  @media (min-width: $breakpoint-tablet) {
    box-shadow: 0 4px 32px rgba(0, 0, 0, 0.12);
    border-radius: 24px;
    background: $color-tertiary;
    width: $breakpoint-tablet;
    max-width: 90vw;
    min-height: 80vh;
    margin: 40px auto;
    padding: 40px;
    align-items: stretch;
    justify-content: center;
    display: flex;
    flex-direction: column;
  }
}

#texts-container {
  z-index: 2; // So the overlay above the img carousel is below the texts
  background: $color-tertiary;
}



.text-container {
  width: 100%;
  // max-width: 520px;
  margin: 0 auto 0 auto;
  box-sizing: border-box;
  word-break: break-word;
}

@media (max-width: $breakpoint-tablet) {
  .text-container {
    // max-width: 100vw;
    padding-left: 8px;
    padding-right: 8px;
  }
}

#footer-made-by {
  text-align: center;
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-bottom: 16px;
}

.centered {
  text-align: center;
}
</style>

<style>
html {
  background: linear-gradient(45deg, #396c7263, #ded8d275);
}
</style>
